import React, { useState, useEffect } from 'react';
import { Mail, Loader2, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { verifyEmail, resendVerification } from '../services/api';

const VerifyEmailPage = () => {
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('token');
    const emailParam = params.get('email');
    
    if (emailParam) {
      setEmail(emailParam);
    }
    
    if (tokenParam) {
      setToken(tokenParam);
      verifyToken(tokenParam);
    } else {
      setLoading(false);
      setError('No verification token provided.');
    }
  }, []);

  const verifyToken = async (tkn) => {
    try {
      await verifyEmail(tkn);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError('Please enter your email address to resend verification.');
      return;
    }
    
    setResending(true);
    setError('');
    try {
      await resendVerification(email);
      setResendSuccess(true);
    } catch {
      setError('Failed to resend verification email.');
    } finally {
      setResending(false);
    }
  };

  const handleBackToLogin = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#393d8a] via-[#393d8a]/90 to-[#30a34b]/80 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_helpdesk-hub-41/artifacts/0bvfy7jq_1_Post_Aruba_Logo.png" 
            alt="Post Aruba Logo" 
            className="w-32 h-32 mx-auto mb-4 object-contain"
          />
          <h1 className="text-3xl font-bold text-white">Post Aruba</h1>
          <p className="text-white/80 mt-1">Support Center</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 animate-spin text-[#393d8a] mx-auto mb-4" />
              <p className="text-gray-500">Verifying your email...</p>
            </div>
          ) : success ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h2>
              <p className="text-gray-500 mb-6">
                Your email has been successfully verified. You can now log in to your account.
              </p>
              <Button
                onClick={handleBackToLogin}
                className="w-full h-12 bg-[#393d8a] hover:bg-[#393d8a]/90 text-white font-semibold"
              >
                Go to Login
              </Button>
            </div>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h2>
              <p className="text-gray-500 mb-6">{error}</p>
              
              {resendSuccess ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-6">
                  <p className="text-green-700 text-sm">
                    A new verification email has been sent. Please check your inbox.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email to resend"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#393d8a] focus:border-transparent"
                    />
                  </div>
                  
                  <Button
                    onClick={handleResendVerification}
                    disabled={resending}
                    className="w-full h-12 bg-[#eeb224] hover:bg-[#eeb224]/90 text-white font-semibold mb-4"
                  >
                    {resending ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-5 h-5 mr-2" />
                        Resend Verification Email
                      </>
                    )}
                  </Button>
                </>
              )}
              
              <button
                onClick={handleBackToLogin}
                className="text-gray-500 hover:text-gray-700 text-sm flex items-center justify-center gap-1 mx-auto"
              >
                <ArrowLeft className="w-4 h-4" /> Back to Login
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-white/60 text-sm mt-6">
          © {new Date().getFullYear()} Post Aruba. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
